import { FC, useState } from "react";
import { CheckCircle, Cancel, Edit, TaskAlt } from "@mui/icons-material";
import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Button
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Colors } from "../../../../theme";
import { getPriorityColor } from "../../counterparties/dataGrid/hooks/useCounterpartyColumns";
import { TradingCounterParty } from "../../../../api/Api";
import { EditCounterpartyDialog } from "./editCounterpartyDialog";
import {
  canEditCounterparty,
  canManageSignOff
} from "../../../../auth/permissions";
import { useRoles } from "../../../../auth/useRoles";
import { useEntityBasePath } from "../../../../utils/entityPaths";
import { EditMergerDialog } from "../../../mergers/editMergerDialog";

type Props = {
  specificCounterparty: TradingCounterParty;
};

export const InfoCard: FC<Props> = ({ specificCounterparty }) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const roles = useRoles();
  const isMerger = Boolean(specificCounterparty.isMerger);
  const showEdit = canEditCounterparty(roles, isMerger);
  const canSignOff = canManageSignOff(roles, isMerger);
  const navigate = useNavigate();
  const basePath = useEntityBasePath("counterparty");

  const signOffStatus = specificCounterparty.signOff?.status;
  const signOffButtonLabel =
    signOffStatus === "completed"
      ? "VIEW SIGN OFF"
      : signOffStatus === "in_progress"
        ? "SIGN OFF IN PROGRESS"
        : "COMPLETE SIGN OFF";
  const showSignOffEntry = canSignOff || Boolean(signOffStatus);

  const handleSignOffClick = () => {
    navigate(`${basePath}/${specificCounterparty.id}/signoff`);
  };

  return (
    <>
      <Grid position="relative" container spacing={3}>
        <Box
          sx={{
            position: { xs: "static", md: "absolute" },
            top: { md: "16px" },
            right: { md: "16px" },
            bottom: { md: "16px" },
            display: "flex",
            flexDirection: { xs: "row", md: "column" },
            justifyContent: { xs: "flex-end", md: "space-between" },
            flexWrap: "wrap",
            gap: 1,
            alignItems: { xs: "center", md: "flex-end" },
            zIndex: 1,
            marginBottom: { xs: 1, md: 0 },
            paddingX: { xs: 1, md: 0 }
          }}
        >
          {showEdit && (
            <Button
              endIcon={<Edit />}
              onClick={() => setEditDialogOpen(true)}
              variant="outlined"
            >
              EDIT
            </Button>
          )}
          {showSignOffEntry && (
            <Button
              endIcon={<TaskAlt />}
              onClick={handleSignOffClick}
              variant="contained"
              sx={
                signOffButtonLabel === "VIEW SIGN OFF"
                  ? {
                      backgroundColor: Colors.NICE_GREEN,
                      "&:hover": { backgroundColor: Colors.NICE_GREEN }
                    }
                  : signOffButtonLabel === "SIGN OFF IN PROGRESS"
                    ? {
                        backgroundColor: Colors.MFT_ORANGE,
                        "&:hover": { backgroundColor: Colors.MFT_ORANGE }
                      }
                    : undefined
              }
            >
              {signOffButtonLabel}
            </Button>
          )}
        </Box>
        <Grid size={{ xs: 12, md: 12 }} padding={1}>
          <Card elevation={5}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: { xs: 3, md: 0 }
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    flex: 1
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      marginBottom: 2
                    }}
                  >
                    Basic Information
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {/* The rest of the merger setup (the Market Access answers and documents)
                        is shown in the Compliance lifecycle. */}
                    {isMerger && (
                      <Box display="flex" gap={3}>
                        <Typography
                          flex="1"
                          variant="caption"
                          color="text.secondary"
                        >
                          Merger Description
                        </Typography>
                        <Typography
                          flex="2.5"
                          variant="body2"
                          sx={{ whiteSpace: "pre-wrap" }}
                        >
                          {specificCounterparty.mergerDescription || "N/A"}
                        </Typography>
                      </Box>
                    )}

                    <Box display="flex" gap={3}>
                      <Typography
                        flex="1"
                        variant="caption"
                        color="text.secondary"
                      >
                        Counterparty Type
                      </Typography>
                      <Typography flex="2.5" variant="body2">
                        {specificCounterparty.counterpartyType || "N/A"}
                      </Typography>
                    </Box>

                    <Box display="flex" gap={3}>
                      <Typography
                        flex="1"
                        variant="caption"
                        color="text.secondary"
                      >
                        Trading Team
                      </Typography>
                      <Typography flex="2.5" variant="body2">
                        {specificCounterparty.tradingTeam || "N/A"}
                      </Typography>
                    </Box>

                    <Box display="flex" gap={3}>
                      <Typography
                        flex="1"
                        variant="caption"
                        color="text.secondary"
                      >
                        Contracting Entity
                      </Typography>
                      <Typography flex="2.5" variant="body2">
                        {specificCounterparty.contractingEntity ||
                          "There should be something here - contact Tech"}
                      </Typography>
                    </Box>

                    <Box display="flex" gap={3}>
                      <Typography
                        flex="1"
                        variant="caption"
                        color="text.secondary"
                      >
                        MFT Contact Person
                      </Typography>
                      <Typography flex="2.5" variant="body2">
                        {specificCounterparty.contactPersonTradingTeam || "N/A"}
                      </Typography>
                    </Box>

                    <Box display="flex" gap={3}>
                      <Typography
                        flex="1"
                        variant="caption"
                        color="text.secondary"
                      >
                        Counterparty Contact Name
                      </Typography>
                      <Typography flex="2.5" variant="body2">
                        {specificCounterparty.counterpartyContactName || "N/A"}
                      </Typography>
                    </Box>
                    <Box display="flex" gap={3}>
                      <Typography
                        flex="1"
                        variant="caption"
                        color="text.secondary"
                      >
                        Counterparty Contact Email
                      </Typography>
                      <Typography flex="2.5" variant="body2">
                        {specificCounterparty.counterpartyContactEmail || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    flex: 1
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      marginBottom: 2
                    }}
                  >
                    Trading Information
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box display="flex" gap={3}>
                      <Typography
                        flex="1"
                        variant="caption"
                        color="text.secondary"
                      >
                        Product Type
                      </Typography>
                      <Typography flex="3" variant="body2">
                        {specificCounterparty.productType || "N/A"}
                      </Typography>
                    </Box>

                    <Box display="flex" gap={3}>
                      <Typography
                        flex="1"
                        variant="caption"
                        color="text.secondary"
                      >
                        Venue
                      </Typography>
                      <Typography flex="3" variant="body2">
                        {specificCounterparty.venue || "N/A"}
                      </Typography>
                    </Box>

                    <Box display="flex" gap={3}>
                      <Typography
                        flex="1"
                        variant="caption"
                        color="text.secondary"
                      >
                        Settlement Type
                      </Typography>
                      <Typography flex="3" variant="body2">
                        {specificCounterparty.tradingSettlementType || "N/A"}
                      </Typography>
                    </Box>

                    <Box display="flex" gap={3}>
                      <Typography
                        flex="1"
                        variant="caption"
                        color="text.secondary"
                      >
                        Agreement Type
                      </Typography>
                      <Typography flex="3" variant="body2">
                        {specificCounterparty.agreementType || "N/A"}
                      </Typography>
                    </Box>

                    <Box display="flex" gap={3}>
                      <Typography
                        flex="1"
                        variant="caption"
                        color="text.secondary"
                      >
                        Transaction Type
                      </Typography>
                      <Box flex="3">
                        <Typography flex="3" variant="body2">
                          {specificCounterparty.transactionType === "one-off"
                            ? "One-off"
                            : "Recurring"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    flex: 1
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      marginBottom: 2
                    }}
                  >
                    Deal & Risk Information
                  </Typography>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box display="flex" gap={2}>
                      <Typography
                        flex="1"
                        variant="caption"
                        color="text.secondary"
                      >
                        Priority
                      </Typography>
                      <Box flex="3">
                        <Chip
                          label={
                            specificCounterparty.urgent
                              ? "URGENT"
                              : "Not urgent"
                          }
                          size="small"
                          variant="filled"
                          sx={{
                            ...getPriorityColor(specificCounterparty.urgent)
                          }}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography
                        flex="1"
                        variant="caption"
                        color="text.secondary"
                      >
                        Mandate Granted
                      </Typography>
                      <Box flex="3">
                        {specificCounterparty.mandate ? (
                          <CheckCircle
                            sx={{ fill: Colors.NICE_GREEN }}
                            fontSize="small"
                          />
                        ) : (
                          <Cancel color="error" fontSize="small" />
                        )}
                      </Box>
                    </Box>

                    <Box display="flex" gap={2}>
                      <Typography
                        flex="1"
                        variant="caption"
                        color="text.secondary"
                      >
                        Expected Deal Volume
                      </Typography>
                      <Typography flex="3" variant="body2">
                        {specificCounterparty.dealVolume
                          ? `${specificCounterparty.dealVolume} ${specificCounterparty.unit || ""}`
                          : "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {showEdit &&
        (isMerger ? (
          <EditMergerDialog
            open={editDialogOpen}
            onClose={() => setEditDialogOpen(false)}
            kind="counterparty"
            entity={specificCounterparty}
          />
        ) : (
          <EditCounterpartyDialog
            open={editDialogOpen}
            onClose={() => setEditDialogOpen(false)}
            specificCounterparty={specificCounterparty}
          />
        ))}
    </>
  );
};
